import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Globe, Shield, CheckCircle, FileText, AlertCircle, Download, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { API, LOJACK_LOGO } from "../config";
import { ContractModal, DigitalCard, WalletButtons, SigningStepper, DEFAULT_TRANSLATIONS } from "../components/wallet";
import { GDPRPreferences } from "../components/wallet/GDPRPreferences";
import { TelcaVoIPBadge } from "../components/shared/TelcaVoIPBadge";
import { PhoneInput } from "../components/shared/PhoneInput";
import { COUNTRY_PREFIXES, getPrefixByCountry, getLocalizedCountryName, formatLocalizedDate } from "../constants/countries";
import { validateCodiceFiscale, deriveBirthDateFromCF } from "../utils/fiscalCode";
import { deriveForeignBirthCountryFromCF } from "../utils/belfioreForeign";
import { CountryCombobox } from "../components/shared/CountryCombobox";
import { useActiveInterval, useDeferredEffect } from "../hooks/useWalletEffects";
import { validateFiscalCode, validateVat, getFiscalCodeInfo, getVatInfo } from "../utils/fiscalCodeMulti";
import { translateValidationError } from "../utils/validationI18n";
import { DatePickerField } from "../components/shared/DatePickerField";
import { getProvinceConfig } from "../utils/provinceConfig";
import { getCustomerFormI18n } from "../translations/customerForm";
import { getCustomerGreetingName } from "../utils/customerName";
import CorrectionRequestModal from "../components/CorrectionRequestModal";
import CorrectionPendingState from "../components/CorrectionPendingState";

// Customer Wallet form/preferences translations — 18 languages, deferred to
// `/translations/customerForm.js` for keepability. Activated by `activeLang`,
// the locally-switched UI language (not the customer's commercial country).
const getFormLang = (code) => getCustomerFormI18n(code);

// Hide the inline vCard-save QR on mobile devices (where the user can't
// scan their own screen). Mirrors the same heuristic used by
// `components/wallet/WalletButtons.js::_isDesktop` for the Apple/Google
// wallet QRs — single source of truth would be nicer but the helper
// is 8 lines and duplication is cheap.
const _isDesktopForQr = () => {
  if (typeof window === "undefined") return true;
  try {
    if (window.matchMedia?.("(max-width: 768px)")?.matches) return false;
    if ("ontouchstart" in window) return false;
    if (/iPad|iPhone|iPod|Android/i.test(navigator.userAgent || "")) return false;
  } catch (_) { /* SSR safety */ }
  return true;
};

// Build the vCard 3.0 payload to embed inside the QR. MUST stay in sync
// with the backend version in `routes/customers.py::download_vcard` so
// the QR scan and the "Save to Contacts" button produce the same record.
const _buildOperationCenterVcard = (oc) => {
  if (!oc?.phone) return null;
  const phoneRaw = String(oc.phone).replace(/[\s-]/g, "");
  const label = oc.label || "Centrale Operativa";
  const country = oc.country || "";
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:LoJack - ${label}`,
    "ORG:LoJack | Tracker",
    `TEL;TYPE=WORK,VOICE:${phoneRaw}`,
    `NOTE:${label}${country ? ` - ${country}` : ""}`,
    "URL:https://lojack.eu/lojacktrackerpassive/",
    "END:VCARD",
  ].join("\r\n");
};

const COUNTRY_OPTIONS = [
  { code: 'IT', name: 'Italia', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: 'FR', name: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'ES', name: 'Espana', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'DE', name: 'Deutschland', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'UK', name: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'PT', name: 'Portugal', flag: '\u{1F1F5}\u{1F1F9}' },
  { code: 'BE', name: 'Belgique / Belgie', flag: '\u{1F1E7}\u{1F1EA}' },
  { code: 'NL', name: 'Nederland', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: 'PL', name: 'Polska', flag: '\u{1F1F5}\u{1F1F1}' },
  { code: 'SE', name: 'Sverige', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: 'NO', name: 'Norge', flag: '\u{1F1F3}\u{1F1F4}' },
  { code: 'FI', name: 'Suomi', flag: '\u{1F1EB}\u{1F1EE}' },
  { code: 'DK', name: 'Danmark', flag: '\u{1F1E9}\u{1F1F0}' },
  { code: 'CZ', name: 'Ceska republika', flag: '\u{1F1E8}\u{1F1FF}' },
  { code: 'GR', name: 'Ellada', flag: '\u{1F1EC}\u{1F1F7}' },
  { code: 'CH', name: 'Schweiz / Suisse', flag: '\u{1F1E8}\u{1F1ED}' },
  { code: 'GBZ', name: 'Gibraltar', flag: '\u{1F1EC}\u{1F1EE}' },
  { code: 'IRL', name: 'Ireland', flag: '\u{1F1EE}\u{1F1EA}' },
];

// Form language selector — endonyms (the language NAME written in
// its own language). Per req. Fabrizio 08/06/2026 we only expose the
// 6 languages that are professionally maintained as native packs;
// every other customer locale falls back to UK English at runtime.
// The `code` is still the ISO country code so the existing i18n
// resolver (getFormLang) keeps working unchanged.
//
// To add more native languages in the future just append the new
// entry here AND replace the corresponding `null` in the 4 dicts
// (customerForm.js, constants.js, GDPRPreferences.js, OTPSignature.js)
// plus remove the code from the backend _ALIASES fallback maps.
const FORM_LANGUAGES = [
  { code: 'IT', name: 'Italiano',  flag: '\u{1F1EE}\u{1F1F9}' },
  { code: 'UK', name: 'English',   flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'FR', name: 'Français',  flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'ES', name: 'Español',   flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'DE', name: 'Deutsch',   flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'PL', name: 'Polski',    flag: '\u{1F1F5}\u{1F1F1}' },
  { code: 'PT', name: 'Português', flag: '\u{1F1F5}\u{1F1F9}' },
  { code: 'NL', name: 'Nederlands', flag: '\u{1F1F3}\u{1F1F1}' },
];

const CustomerWalletPage = () => {
  const { customerId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [walletUrls, setWalletUrls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [vehicleLocation, setVehicleLocation] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [translations, setTranslations] = useState(DEFAULT_TRANSLATIONS);
  const [operationCenter, setOperationCenter] = useState(null);
  const [contractSigned, setContractSigned] = useState(false);
  const [contractPdfUrl, setContractPdfUrl] = useState(null);
  const [signingContract, setSigningContract] = useState(false);
  const [changingCountry, setChangingCountry] = useState(false);
  const [walletStep, setWalletStep] = useState('form');
  // Live progress % of contract PDF reading (0-100), used by SigningStepper.
  // Fed via ContractModal → onReadProgress callback (Mara/Zorzutti req.
  // 24/07/2026: show "N% del contratto letto" during step 3).
  const [contractReadPct, setContractReadPct] = useState(0);
  const [personalData, setPersonalData] = useState({
    first_name: '', last_name: '',
    address: '', civic_number: '', zip_code: '', city: '', province: '', address_country: 'IT',
    birth_date: '', birth_place: '', birth_province: '', birth_country: 'IT',
    tax_code: '', vat_number: '', company_name: '',
    phone2_prefix: '+39', phone2: ''
  });
  const [showDataConfirm, setShowDataConfirm] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [dataConfirmChecked, setDataConfirmChecked] = useState(false);
  const [googleWalletLoading, setGoogleWalletLoading] = useState(false);
  const [appleWalletLoading, setAppleWalletLoading] = useState(false);
  const [applePassReady, setApplePassReady] = useState(null);
  const [googleWalletUrl, setGoogleWalletUrl] = useState(null);
  const [viesLoading, setViesLoading] = useState(false);
  const [viesResult, setViesResult] = useState(null);
  // Pre-signature correction request state (Fabrizio CR 31/07/2026).
  // When the customer flags an error in the dealer-pre-filled data
  // the wallet form is halted server-side; the frontend renders the
  // CorrectionPendingState until the dealer resolves the request.
  const [correctionPending, setCorrectionPending] = useState(false);
  const [correctionRequest, setCorrectionRequest] = useState(null);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  // Country-level "required fields" policy — set by the LoJack IT Admin
  // via the CountryPolicyWidget on the IT portal. Pre-18/06/2026 this
  // form ignored the policy and always treated birth_date/birth_place/
  // birth_province as mandatory regardless of the admin's choice
  // (bug reported by Mara during stand-up 15/06). Now we read the
  // policy and let it drive the `required` attribute. The policy is
  // resolved against ``customer.country`` (commercial country, fixed
  // by the dealer), NOT the UI form language.
  //
  // Mara CR 20/02/2026 — Zorzutti reopened the bug: the previous fix
  // defaulted to ``{birth_date: true, birth_place: true}`` so that
  // during the network round-trip (~150-300ms) the fields would
  // flash the asterisk and THEN drop it. Now we default to OPT-OUT
  // (false) which is both GDPR-safer ("don't ask unless you know
  // you must") AND avoids the flash. We also cache the resolved
  // policy in localStorage so returning customers get an instant,
  // flicker-free render.
  const POLICY_CACHE_KEY_BASE = "lojack:country_policy:v1";
  const _readCachedPolicy = (cc) => {
    try {
      if (typeof window === "undefined" || !cc) return null;
      const raw = window.localStorage.getItem(POLICY_CACHE_KEY_BASE);
      const parsed = raw ? JSON.parse(raw) : {};
      const entry = parsed && parsed[cc];
      if (entry && typeof entry === "object") return entry;
    } catch { /* corrupt cache → fall through to fetch */ }
    return null;
  };
  const _writeCachedPolicy = (cc, value) => {
    try {
      if (typeof window === "undefined" || !cc) return;
      const raw = window.localStorage.getItem(POLICY_CACHE_KEY_BASE);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed[cc] = value;
      window.localStorage.setItem(POLICY_CACHE_KEY_BASE, JSON.stringify(parsed));
    } catch { /* ignore quota */ }
  };
  const [countryPolicy, setCountryPolicy] = useState({
    tax_code: false, birth_date: false, birth_place: false,
    // Bug Fabrizio 28/02/2026: la pagina OTP mostrava sempre "email +
    // mobile" anche quando la Per-Country Policy era settata su
    // "Email only". `otp_delivery_channel` viene letto dall'API
    // /api/country-config (campo già esposto dal backend) e propagato
    // a OTPSignature via ContractModal per condizionare la UI del
    // canale di consegna del codice.
    otp_delivery_channel: 'both',
  });
  // Local UI language for the "Compila i tuoi dati" step — decoupled from
  // customer.country. Changing this dropdown ONLY swaps form labels, it
  // does NOT touch the backend (per client requirement). The UI language
  // also propagates to ContractModal / OTPSignature / step "complete"
  // via the `uiLanguage` prop and a translations refetch (see effect
  // below). PDFs (contratto + privacy) remain locked to customer.country
  // (dealer-assigned language) per Fabrizio req. 10/06/2026.
  // Persisted across reloads (Fabrizio req. 11/06/2026):
  //   Bug repro: customer with country=IT picks UI=UK at the form step,
  //   completes the signature, lands on step="complete". On reload (or
  //   when re-opening the email link from the welcome message), the
  //   state was rebuilt from scratch → `formLanguage` reset to null →
  //   `activeLang` collapsed back to customer.country (IT) → the entire
  //   page rendered in Italian. Persisting per-customer in localStorage
  //   keeps the choice sticky for the whole flow + post-signature steps.
  const LANG_STORAGE_KEY = customerId ? `lojack-wallet-uilang:${customerId}` : null;
  const [formLanguage, setFormLanguage] = useState(() => {
    try {
      if (typeof window === "undefined" || !LANG_STORAGE_KEY) return null;
      const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
      return stored || null;
    } catch (_) { return null; }
  });
  // Persist every change. We bypass setFormLanguage by exposing a small
  // wrapper that writes localStorage AND updates the state.
  const persistFormLanguage = (value) => {
    setFormLanguage(value);
    try {
      if (typeof window !== "undefined" && LANG_STORAGE_KEY) {
        if (value) window.localStorage.setItem(LANG_STORAGE_KEY, value);
        else window.localStorage.removeItem(LANG_STORAGE_KEY);
      }
    } catch (_) { /* private mode / quota — silently ignore */ }
  };
  const activeLang = formLanguage || customer?.country || 'IT';
  const tForm = getFormLang(activeLang);

  // Refresh the backend-served translations pack whenever the UI
  // language changes — so labels on step "complete" (Card Pronta,
  // "Aggiungi a Apple Wallet", "Centrale Operativa", etc.) also follow
  // the in-app selection rather than the dealer-assigned country.
  useEffect(() => {
    if (!customerId || !formLanguage) return;  // skip initial mount
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/customers/${customerId}/translations?lang=${formLanguage}`
        );
        if (!cancelled && res.data?.translations) {
          setTranslations(res.data.translations);
        }
      } catch (_) { /* keep previous translations on error */ }
    })();
    return () => { cancelled = true; };
  }, [formLanguage, customerId]);

  // Fetch the country-level required-fields policy once we know the
  // customer's commercial country. Without this the form keeps the
  // pre-fix default (all 3 fields required) → bug reported by Mara
  // 15/06/2026 ("tax id e nascita sempre obbligatori anche se admin ha
  // detto NO"). The endpoint is public (no auth) by design so the
  // customer wallet page can call it.
  //
  // Mara CR 20/02/2026 — hydrate from localStorage cache BEFORE the
  // fetch so a returning customer never sees the asterisk flash.
  // Background fetch then refreshes the cache for the next visit.
  useEffect(() => {
    const cc = (customer?.country || '').toUpperCase();
    if (!cc) return;
    const cached = _readCachedPolicy(cc);
    if (cached) setCountryPolicy(cached);
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/country-config`);
        const entry = (res.data?.countries || []).find(c => c.code === cc || c.country === cc);
        if (cancelled || !entry?.required_fields) return;
        const next = {
          tax_code: Boolean(entry.required_fields.tax_code),
          birth_date: Boolean(entry.required_fields.birth_date),
          birth_place: Boolean(entry.required_fields.birth_place),
          // Defaults to 'both' if the admin hasn't customised it — keeps
          // the historical behaviour for countries without explicit policy.
          otp_delivery_channel: entry.otp_delivery_channel || 'both',
        };
        setCountryPolicy(next);
        _writeCachedPolicy(cc, next);
      } catch (_) {
        // Keep the last-known-good (cached or default) policy on
        // network error so we never silently force-relax the form.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.country]);

  const handleViesLookup = async () => {
    const vatRaw = (personalData.vat_number || '').trim();
    const country = (personalData.address_country || customer?.country || 'IT').toUpperCase();
    if (!vatRaw) {
      toast.error('Inserisci prima la P.IVA');
      return;
    }
    setViesLoading(true);
    setViesResult(null);
    try {
      const res = await axios.get(`${API}/vies/lookup`, { params: { country, vat: vatRaw } });
      setViesResult(res.data);
      toast.success('Dati aziendali trovati su VIES');
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) toast.error('P.IVA non trovata su VIES');
      else if (status === 400) toast.error('Paese non supportato da VIES (solo paesi UE)');
      else if (status === 502) toast.error('Servizio VIES temporaneamente non disponibile');
      else toast.error(err.response?.data?.detail || 'Errore verifica VIES');
    } finally {
      setViesLoading(false);
    }
  };

  const applyViesData = () => {
    if (!viesResult) return;
    setPersonalData(prev => ({
      ...prev,
      company_name: viesResult.name || prev.company_name,
      address: viesResult.address?.street || prev.address,
      zip_code: viesResult.address?.zip_code || prev.zip_code,
      city: viesResult.address?.city || prev.city,
      province: viesResult.address?.province || prev.province,
    }));
    setViesResult(null);
    toast.success('Dati VIES applicati al form');
  };

  const handleCountryChange = async (newCountry) => {
    if (!newCountry || newCountry === customer?.country) return;

    // Req. Fabrizio 11/06/2026 — Decoupling SOC picker from UI language:
    // changing the operating-centre dropdown in step "complete" must ONLY
    // swap the phone number on the digital card (and the SOC label on the
    // vCard saved to the wallet). It MUST NOT re-translate the whole page.
    //
    // Without this lock, since `activeLang = formLanguage || customer.country`,
    // a customer who never explicitly picked a UI language (formLanguage == null)
    // would see the entire UI flip into the new SOC's language — which is the
    // exact behaviour the customer is complaining about. We capture the
    // currently-displayed language into `formLanguage` BEFORE we touch
    // customer.country, freezing the UI in place.
    if (!formLanguage) {
      persistFormLanguage(customer?.country || 'IT');
    }

    setChangingCountry(true);
    try {
      const res = await axios.put(`${API}/customers/${customerId}/country`, { country: newCountry });
      setCustomer(prev => ({ ...prev, country: newCountry }));
      // INTENTIONALLY NOT calling setTranslations(res.data.translations) here:
      // the SOC change must not re-translate the UI. The i18n effect that
      // watches `formLanguage` will keep the texts locked to the customer's
      // original choice (see useEffect above).
      //
      // Update ONLY the phone number on the card (the customer-visible
      // SOC number). DELIBERATELY DO NOT overwrite `label` / `country`
      // (e.g. "Centrale Opérationnelle 24/7", "France") with the values
      // returned by the API — those would be in the new SOC's language
      // and would partially leak into the customer's UI, breaking the
      // "only the number changes" requirement (Fabrizio 11/06/2026).
      setOperationCenter(prev => ({
        ...(prev || {}),
        phone: res.data.phone,
      }));
      toast.success(res.data.country_name ? `${res.data.country_name} - OK` : 'Centrale operativa aggiornata');
    } catch (err) {
      toast.error('Errore aggiornamento centrale operativa');
    } finally {
      setChangingCountry(false);
    }
  };

  const handlePersonalDataSubmit = (e) => {
    if (e) e.preventDefault();

    const country = (personalData.address_country || customer?.country || "IT").toUpperCase();
    const isBusiness = (customer?.customer_type || 'private') === 'business';

    // For private customers, validate the customer's CF (read-only field but
    // still defensively re-checked). For business, validate the VAT instead.
    if (!isBusiness && personalData.tax_code) {
      const cfCheck = validateFiscalCode(personalData.tax_code, country);
      if (!cfCheck.valid) {
        toast.error(translateValidationError(cfCheck.error, activeLang));
        return;
      }
    }
    if (isBusiness && personalData.vat_number) {
      const vatCheck = validateVat(personalData.vat_number, country);
      if (!vatCheck.valid) {
        toast.error(translateValidationError(vatCheck.error, activeLang));
        return;
      }
    }

    if (!showDataConfirm) {
      setShowDataConfirm(true);
      return;
    }
    setShowDataConfirm(false);
    setSavingData(true);

    // Strip whitespace from optional Phone 2 before submitting
    const payload = {
      ...personalData,
      phone2: (personalData.phone2 || '').replace(/\s+/g, ''),
    };

    axios.put(`${API}/customers/${customerId}/personal-data`, payload)
      .then(async () => {
        // Fabrizio CR 06/08/2026 — register the explicit pre-signature
        // confirmation (timestamp + IP + tamper-evident hash). No diff
        // tracking here: dealer-prefilled fields (email/phone/tax_code)
        // are already `<input disabled>` in the dealer-prefilled widget,
        // so the customer cannot autonomously modify them. If the
        // customer sees a wrong value they use the existing "Segnala
        // errore" button (correction modal), which blocks the procedure
        // and notifies the dealer.
        //
        // Non-blocking: if this call fails the sign endpoint will re-check
        // `data_confirmed_at` and reject the signature with a proper
        // localised error.
        try {
          await axios.post(
            `${API}/customers/${customerId}/confirm-pre-signature-data`,
            {},
          );
        } catch (err) {
          console.warn('pre-signature confirmation registration failed', err);
        }
        toast.success('Dati salvati');
        setCustomer(prev => ({ ...prev, ...payload, personal_data_completed: true, data_confirmed_at: new Date().toISOString() }));
        // Go to GDPR preferences step (new intermediate step)
        setWalletStep('preferences');
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Errore salvataggio'))
      .finally(() => setSavingData(false));
  };

  const fetchVehicleLocation = useCallback(async () => {
    try {
      setLocationLoading(true);
      const response = await axios.get(`${API}/customers/${customerId}/location`);
      setVehicleLocation(response.data);
    } catch (error) {
      console.error("Error fetching location:", error);
    } finally {
      setLocationLoading(false);
    }
  }, [customerId]);

  const fetchLocationHistory = async () => {
    try {
      const response = await axios.get(`${API}/customers/${customerId}/location/history?hours=24`);
      setLocationHistory(response.data.locations || []);
      setShowHistory(true);
    } catch (error) {
      toast.error("Errore nel caricamento dello storico");
    }
  };

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${API}/customers/${customerId}`);
      setCustomer(response.data);
      setTermsAccepted(response.data.terms_accepted);
      setContractSigned(response.data.contract_signed);

      // Check for a pending pre-signature correction request. If one
      // exists the wallet form must stay hidden (Fabrizio CR
      // 31/07/2026 — option 2.a full block until dealer resolves).
      // Skipped for signed contracts because those go through the
      // OTP self-update flow instead.
      if (!response.data.contract_signed) {
        try {
          const corr = await axios.get(
            `${API}/customers/${customerId}/wallet/correction-status`
          );
          if (corr.data?.pending && Array.isArray(corr.data?.requests) && corr.data.requests.length > 0) {
            setCorrectionPending(true);
            setCorrectionRequest(corr.data.requests[0]);
            setLoading(false);
            return; // short-circuit: no need to build the form state
          } else {
            setCorrectionPending(false);
            setCorrectionRequest(null);
          }
        } catch (_e) {
          // Non-fatal: if the correction-status endpoint fails we
          // fall through to the normal form. The dealer approve/
          // reject path will unblock the customer on the next open.
        }
      }
      
      if (response.data.contract_signed || response.data.terms_accepted) {
        setWalletStep('complete');
      } else if (response.data.gdpr_preferences) {
        // Personal data + GDPR done → ready for contract step
        setWalletStep('contract');
      } else if (response.data.personal_data_completed) {
        // Personal data done but GDPR preferences not yet → preferences step
        setWalletStep('preferences');
      } else {
        // Req. Fabrizio 08/06/2026 — Skip the legacy "Benvenuto!" landing
        // page (Pag 5 feedback Zorzutti): when a fresh customer clicks
        // the email link the FIRST screen must be the personal-data form,
        // not an intermediate welcome page that just shows another CTA.
        setWalletStep('form');
      }

      // Always merge whatever the dealer pre-filled into the customer's form
      // state. The dealer may have entered any subset of: company_name,
      // vat_number, tax_code, first_name, last_name, address, etc. The
      // previous guard `if (response.data.first_name)` excluded business
      // customers (who have no first_name) → "Ragione Sociale" appeared
      // empty even though the dealer had typed it. Fix Bug A (req. Zorzutti):
      // perform a defensive merge in all cases.
      setPersonalData(prev => ({
        ...prev,
        first_name: response.data.first_name || prev.first_name || '',
        last_name: response.data.last_name || prev.last_name || '',
        address: response.data.address || prev.address || '',
        civic_number: response.data.civic_number || prev.civic_number || '',
        zip_code: response.data.zip_code || prev.zip_code || '',
        city: response.data.city || prev.city || '',
        province: response.data.province || prev.province || '',
        address_country: response.data.address_country || response.data.country || prev.address_country || 'IT',
        birth_date: response.data.birth_date || prev.birth_date || '',
        birth_place: response.data.birth_place || prev.birth_place || '',
        birth_province: response.data.birth_province || prev.birth_province || '',
        birth_country: response.data.birth_country
          || response.data.address_country
          || response.data.country
          || prev.birth_country
          || 'IT',
        tax_code: response.data.tax_code || prev.tax_code || '',
        vat_number: response.data.vat_number || prev.vat_number || '',
        company_name: response.data.company_name || prev.company_name || '',
        phone2_prefix: response.data.phone2_prefix
          || getPrefixByCountry(response.data.country || 'IT'),
        phone2: response.data.phone2 || prev.phone2 || '',
      }));
      
      const transResponse = await axios.get(
        `${API}/customers/${customerId}/translations${formLanguage ? `?lang=${formLanguage}` : ''}`
      );
      setTranslations(transResponse.data.translations || DEFAULT_TRANSLATIONS);
      setOperationCenter({
        phone: transResponse.data.phone,
        label: transResponse.data.label,
        country: transResponse.data.country
      });

      try {
        // Use the customer-aware endpoint so the CMS can pick the
        // contract version specific to this customer's product
        // (Passive 1/3/4) and language. Falls back to the legacy
        // static URL when no CMS doc is active.
        const contractResponse = await axios.get(`${API}/customers/${customerId}/contract-pdf-url`);
        setContractPdfUrl(contractResponse.data.pdf_url);
      } catch (e) {
        console.error("Error fetching contract URL:", e);
      }
      
      if (response.data.terms_accepted) {
        // Propagate the active UI language so the wallet-urls endpoint
        // returns translations matching the customer's chosen UI
        // language — NOT their billing country. Without `?lang=`, the
        // response would always re-set `translations` to the Italian
        // pack and silently overwrite the UK pack the form-language
        // effect just installed (Fabrizio 11/06/2026 bug repro).
        const urlsResponse = await axios.get(
          `${API}/customers/${customerId}/wallet-urls${formLanguage ? `?lang=${formLanguage}` : ''}`
        );
        setWalletUrls(urlsResponse.data);
        if (urlsResponse.data.translations) {
          setTranslations(urlsResponse.data.translations);
        }
        fetchVehicleLocation();
      }
    } catch (error) {
      toast.error("Cliente non trovato");
    } finally {
      setLoading(false);
    }
  };

  // Fetch automatico del URL Google Wallet appena il cliente arriva sullo
  // step "complete" (post-firma). Il QR sotto "Aggiungi a Google Wallet"
  // deve apparire SUBITO, simmetrico al QR Apple — l'utente non deve
  // cliccare il bottone per vedere il QR (Fabrizio req. 12/06/2026).
  //
  // L'effect riparte ogni volta che `termsAccepted` o `customer.signed_at`
  // cambiano, così il fetch viene RITENTATO automaticamente subito dopo
  // la firma. Il backend ritorna 400 finché il cliente non ha firmato:
  // teniamo il catch silenzioso fino a quando uno dei due flag flippa.
  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    axios.get(`${API}/wallet/${customerId}/google-wallet-url`)
      .then(res => {
        if (!cancelled && res.data?.save_url) {
          setGoogleWalletUrl(res.data.save_url);
        }
      })
      .catch(err => {
        // Pre-signature 400 is expected; downstream 5xx is logged
        // because it usually indicates a misconfigured Google service
        // account in production.
        const status = err?.response?.status;
        if (status && status !== 400) {
          // eslint-disable-next-line no-console
          console.warn("[google-wallet-url auto-fetch] failed:", status, err?.message);
        }
      });
    return () => { cancelled = true; };
  }, [customerId, termsAccepted, customer?.signed_at]);

  // Auto-derive birth_date + birth_country from Codice Fiscale when valid
  // (Italian customers only). The Codice Fiscale Belfiore code (chars 12-15)
  // starting with "Z" indicates a foreign birthplace → we auto-tick "Nato
  // all'estero" and preselect the country from the ISTAT Z-code table.
  // Routed through `useDeferredEffect` so the React 19 set-state-in-effect
  // rule does not analyse setState calls in this branch.
  useDeferredEffect(() => {
    if (personalData.address_country !== 'IT') return;
    if (!personalData.tax_code || personalData.tax_code.length !== 16) return;
    const cfCheck = validateCodiceFiscale(personalData.tax_code);
    if (!cfCheck.valid) return;
    const derivedDate = deriveBirthDateFromCF(personalData.tax_code);
    if (derivedDate && derivedDate !== personalData.birth_date) {
      setPersonalData(prev => ({ ...prev, birth_date: derivedDate }));
      toast.success(tForm.cf_derived_birth_msg || 'Data di nascita derivata dal Codice Fiscale', { duration: 2500 });
    }
    const foreignIso = deriveForeignBirthCountryFromCF(personalData.tax_code);
    if (foreignIso && foreignIso !== personalData.birth_country) {
      setPersonalData(prev => ({
        ...prev,
        birth_country: foreignIso,
        // Foreign birth → no Italian province applies; wipe any stale value
        // typed before the CF was completed.
        birth_province: '',
      }));
      toast.success(tForm.cf_derived_foreign_msg || 'Nato all\'estero derivato dal Codice Fiscale', { duration: 2500 });
    }
  }, [personalData.tax_code, personalData.address_country]);

  const addToWallet = async (walletType) => {
    if (walletType === 'google') {
      try {
        setGoogleWalletLoading(true);
        const response = await axios.get(`${API}/wallet/${customerId}/google-wallet-url`);
        const saveUrl = response.data.save_url;
        // Popola SUBITO il state — così il <QRCodeSVG/> nel WalletButtons
        // appare anche se l'auto-fetch iniziale è fallito o l'utente
        // arriva su questo step prima che `termsAccepted` venga letto dal
        // backend (req. Fabrizio 11/06/2026: "in produzione clicco ma
        // non appare il QR" — bug confermato).
        setGoogleWalletUrl(saveUrl);
        window.open(saveUrl, '_blank');
        await axios.post(`${API}/customers/${customerId}/add-to-wallet?wallet_type=google`);
        toast.success("Google Wallet aperto - aggiungi la card!");
        fetchCustomer();
      } catch (error) {
        toast.error("Errore nella generazione del pass Google Wallet");
      } finally {
        setGoogleWalletLoading(false);
      }
    } else if (walletType === 'apple') {
      // Trigger a .pkpass download. On iOS Safari the file opens
      // directly inside the Wallet app; on desktop/Android the user
      // gets a downloaded file that they can AirDrop/email to iPhone.
      try {
        setAppleWalletLoading(true);
        const url = `${API}/wallet/${customerId}/apple-pass`;
        // Probe with a HEAD-style fetch first so we can surface useful
        // error messages (e.g. .p12 not uploaded yet, contract not signed)
        // instead of letting Safari swallow them silently.
        const probe = await axios.get(url, { responseType: 'blob', validateStatus: () => true });
        if (probe.status !== 200) {
          let msg = `HTTP ${probe.status}`;
          if (probe.data instanceof Blob) {
            try { msg = JSON.parse(await probe.data.text()).detail || msg; } catch { /* ignore */ }
          }
          toast.error(`Apple Wallet non disponibile: ${msg}`);
          return;
        }
        // Stream the .pkpass to the browser
        const blob = new Blob([probe.data], { type: 'application/vnd.apple.pkpass' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `lojack_${customer?.vehicle_plate || customerId}.pkpass`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href);
        await axios.post(`${API}/customers/${customerId}/add-to-wallet?wallet_type=apple`);
        toast.success("Pass Apple Wallet scaricato — aprilo per aggiungerlo al Wallet");
        fetchCustomer();
      } catch (error) {
        toast.error("Errore nella generazione del pass Apple Wallet");
      } finally {
        setAppleWalletLoading(false);
      }
    }
  };

  // Initial customer load. Routed through `useDeferredEffect` so the
  // React 19 set-state-in-effect rule does not analyse setState calls
  // inside fetchCustomer.
  useDeferredEffect(() => {
    fetchCustomer();
  }, [customerId]);

  // Periodic vehicle-location refresh while contract is signed.
  // Routed through `useActiveInterval` so the React 19 set-state-in-effect
  // rule does not analyse setState calls in fetchVehicleLocation.
  useActiveInterval(fetchVehicleLocation, termsAccepted && !!customerId, 30000);

  // Probe whether the server can actually mint Apple Wallet passes
  // (depends on the .p12 cert being uploaded in the IT Portal). The
  // result drives the Apple Wallet button state in the UI.
  useEffect(() => {
    if (!termsAccepted) return;
    let cancelled = false;
    axios.get(`${API}/wallet/apple-pass/ready`)
      .then(r => { if (!cancelled) setApplePassReady(!!r.data?.ready); })
      .catch(() => { if (!cancelled) setApplePassReady(false); });
    return () => { cancelled = true; };
  }, [termsAccepted]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white">Cliente non trovato</h2>
        </div>
      </div>
    );
  }

  // Pre-signature correction pending → block the entire wallet flow
  // and render the "in attesa" state instead. The customer will get
  // a fresh wallet email once the dealer approves the correction.
  if (correctionPending && correctionRequest) {
    return (
      <CorrectionPendingState
        request={correctionRequest}
        customerEmail={customer?.email}
        translations={{
          title: tForm.correction_pending_title,
          subtitle: tForm.correction_pending_subtitle,
          field_label: tForm.correction_pending_field_label,
          current_label: tForm.correction_pending_current_label,
          requested_label: tForm.correction_pending_requested_label,
          note_label: tForm.correction_pending_note_label,
          submitted_at: tForm.correction_pending_submitted_at,
          what_happens: tForm.correction_pending_what_happens,
          step_1: tForm.correction_pending_step_1,
          step_2: tForm.correction_pending_step_2,
          step_3: tForm.correction_pending_step_3,
          step_4: tForm.correction_pending_step_4,
          email_notice: tForm.correction_pending_email_notice,
          contact_dealer: tForm.correction_pending_contact_dealer,
          close_title: tForm.correction_pending_close_title,
          close_body: tForm.correction_pending_close_body,
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-lojack-black" data-testid="customer-wallet-page">
      {/* Header */}
      <div className="bg-lojack-dark border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center gap-4">
            <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-14" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* STEP: PERSONAL DATA FORM */}
        {walletStep === 'form' && (
          <div className="max-w-2xl mx-auto animate-fade-in">
            <SigningStepper currentStep={1} translations={tForm} />
            {/* Form language switcher — explicitly labeled so the customer
                understands the dropdown changes the FORM language (not the
                operation centre / phone number). Req. Fabrizio 08/06/2026:
                "il selettore in alto deve essere chiaro che serve a
                cambiare la lingua del form/sezione". Sits in a soft red
                bordered banner with a Globe icon, a one-line title and a
                helper sub-line so the affordance is unambiguous. */}
            <div
              className="mb-5 rounded-xl border border-lojack-red/30 bg-gradient-to-br from-red-950/20 to-transparent p-3"
              data-testid="form-language-switcher-banner"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full border border-lojack-red/40 bg-lojack-red/10 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-4 h-4 text-lojack-red" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-[13px] font-semibold leading-tight">
                      {tForm.form_language_label || 'Lingua del modulo'}
                    </p>
                    <p className="text-gray-400 text-[11px] leading-tight mt-0.5 truncate">
                      {tForm.form_language_hint || 'Cambia se preferisci compilare in un\'altra lingua'}
                    </p>
                  </div>
                </div>
                <select
                  value={(() => {
                    // Map current activeLang to the closest native dropdown
                    // entry. Native packs grew to 8 (Fabrizio CR 28/06/2026):
                    // IT/UK/FR/ES/DE/PL/PT/NL. The remaining locales
                    // (SE/NO/FI/DK/CZ/GR/HU/RO/CY/BE/CH/GBZ/IRL) still
                    // resolve via the alias table.
                    const NATIVE = ['IT', 'UK', 'FR', 'ES', 'DE', 'PL', 'PT', 'NL'];
                    const ALIAS_TO_NATIVE = { BE: 'FR', CH: 'DE', GBZ: 'UK', IRL: 'UK' };
                    if (NATIVE.includes(activeLang)) return activeLang;
                    if (ALIAS_TO_NATIVE[activeLang]) return ALIAS_TO_NATIVE[activeLang];
                    return 'UK';  // all other non-native locales fall back to English
                  })()}
                  onChange={(e) => persistFormLanguage(e.target.value)}
                  className="bg-[#1E1E1E] text-gray-200 text-xs border border-gray-700 rounded-lg px-2 py-2 cursor-pointer outline-none focus:border-lojack-red appearance-none pr-7 flex-shrink-0"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
                  data-testid="form-language-selector"
                >
                  {FORM_LANGUAGES.map(l => (
                    <option key={l.code} value={l.code} style={{ backgroundColor: '#1A1A1A', color: '#fff' }}>
                      {l.flag} {l.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="wallet-card">
              <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <FileText size={20} className="text-lojack-red" />
                {tForm.title}
              </h2>
              <p className="text-white text-base font-medium mb-6" data-testid="form-subtitle">
                {tForm.subtitle}
              </p>
              <form onSubmit={handlePersonalDataSubmit} className="space-y-4">
                {(() => {
                  const isBusiness = (customer?.customer_type || 'private') === 'business';
                  const country = (personalData.address_country || customer?.country || "IT").toUpperCase();
                  // Pass activeLang so the document label translates into the
                  // customer-chosen UI language (req. Fabrizio 10/06/2026 —
                  // "Codice Fiscale" → "Italian Fiscal Code" in English UI,
                  // ecc.). The document COUNTRY stays unchanged (Italian
                  // document for an Italian customer).
                  const cfInfo = getFiscalCodeInfo(country, activeLang);
                  const vatInfo = getVatInfo(country, activeLang);
                  return (
                    <>
                      {/* ── Panel Widget: Dati precompilati dal Concessionario ──
                          Req. Zorzutti Stand-up 22/05/2026: il blocco dei dati
                          inseriti dal Dealer è ora un widget dedicato in TOP del
                          form (era in fondo). Read-only — il cliente non può
                          modificare quanto inserito dal concessionario.
                          Fabrizio CR 31/07/2026: aggiunto pulsante "Segnala
                          errore" che apre il modal di richiesta correzione. */}
                      <div
                        className="rounded-xl border border-blue-500/30 bg-gradient-to-br from-blue-950/30 to-transparent p-4"
                        data-testid="dealer-prefilled-widget"
                      >
                        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <Shield size={14} className="text-blue-400" />
                            <p className="text-blue-300 text-[11px] uppercase tracking-wider font-semibold">
                              {tForm.dealer_data_title}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowCorrectionModal(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-700/50 bg-amber-950/30 text-amber-300 hover:bg-amber-900/40 hover:border-amber-600 text-[11px] font-medium transition-colors"
                            data-testid="report-error-btn"
                            title="Segnala un errore nei dati precompilati"
                          >
                            <AlertCircle size={12} />
                            {tForm.report_error_btn || 'Segnala errore'}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-gray-400 text-xs">Email</label>
                            <input value={customer?.email || ''} disabled className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-gray-400 cursor-not-allowed text-sm" data-testid="personal-email-readonly" />
                          </div>
                          <div>
                            <label className="text-gray-400 text-xs">{tForm.phone1}</label>
                            <input value={`${customer?.phone_prefix || ''} ${customer?.phone || ''}`.trim()} disabled className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-gray-400 cursor-not-allowed text-sm" data-testid="personal-phone-readonly" />
                          </div>
                          {/* CF/P.IVA come from the dealer and are NOT editable by the customer */}
                          {!isBusiness && (
                            <div className="col-span-2">
                              <label className="text-gray-400 text-xs">{cfInfo.label}{countryPolicy.tax_code ? '*' : ''}</label>
                              <input
                                value={customer?.tax_code || personalData.tax_code || ''}
                                disabled
                                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-gray-400 cursor-not-allowed uppercase font-mono tracking-wider text-sm"
                                data-testid="personal-tax-readonly"
                              />
                            </div>
                          )}
                          {isBusiness && (
                            <div className="col-span-2">
                              <label className="text-gray-400 text-xs">{vatInfo.label}{countryPolicy.tax_code ? '*' : ''}</label>
                              <input
                                value={customer?.vat_number || personalData.vat_number || ''}
                                disabled
                                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-gray-400 cursor-not-allowed uppercase text-sm"
                                data-testid="personal-vat-readonly"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Identity — depends on customer_type. Business hides Nome+Cognome
                          and shows Ragione Sociale instead. Private hides Ragione Sociale. */}
                      {isBusiness ? (
                        <div>
                          <label className="text-gray-400 text-xs">{tForm.confirm_company}*</label>
                          <input
                            value={personalData.company_name || ''}
                            onChange={e => setPersonalData({...personalData, company_name: e.target.value})}
                            className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none"
                            required
                            data-testid="personal-company-name"
                          />
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-gray-400 text-xs">{tForm.first_name}*</label>
                            <input value={personalData.first_name} onChange={e => setPersonalData({...personalData, first_name: e.target.value})} className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="personal-first-name" />
                          </div>
                          <div>
                            <label className="text-gray-400 text-xs">{tForm.last_name}*</label>
                            <input value={personalData.last_name} onChange={e => setPersonalData({...personalData, last_name: e.target.value})} className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="personal-last-name" />
                          </div>
                        </div>
                      )}

                      {/* Address (common) */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <label className="text-gray-400 text-xs">{tForm.address}*</label>
                          <input value={personalData.address} onChange={e => setPersonalData({...personalData, address: e.target.value})} className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="personal-address" placeholder={tForm.placeholders?.address || "es. Via Roma"} />
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs">{tForm.civic}*</label>
                          <input value={personalData.civic_number} onChange={e => setPersonalData({...personalData, civic_number: e.target.value})} className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="personal-civic" placeholder={tForm.placeholders?.civic || "12/A"} />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-gray-400 text-xs">{tForm.zip}*</label>
                          <input value={personalData.zip_code} onChange={e => setPersonalData({...personalData, zip_code: e.target.value})} className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="personal-zip" placeholder={tForm.placeholders?.zip || "00100"} />
                        </div>
                        <div className="col-span-2">
                          <label className="text-gray-400 text-xs">{tForm.city}*</label>
                          <input value={personalData.city} onChange={e => setPersonalData({...personalData, city: e.target.value})} className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="personal-city" placeholder={tForm.placeholders?.city || "Roma"} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          {(() => {
                            // Country-aware province field (Fabrizio req. 03/06/2026):
                            // Italy / Switzerland use 2-letter codes (MI, ZH); every
                            // other supported country uses a free-text regional name
                            // (Bundesland, Województwo, Département, etc.).
                            const cfg = getProvinceConfig(personalData.address_country, activeLang);
                            return (
                              <>
                                <label className="text-gray-400 text-xs">{cfg.label}*</label>
                                <input
                                  value={personalData.province}
                                  onChange={e => setPersonalData({
                                    ...personalData,
                                    province: cfg.autoUppercase ? e.target.value.toUpperCase() : e.target.value,
                                  })}
                                  className={`w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none ${cfg.autoUppercase ? 'uppercase' : ''}`}
                                  required
                                  maxLength={cfg.maxLength}
                                  placeholder={cfg.placeholder}
                                  pattern={cfg.pattern || undefined}
                                  title={cfg.helperText || undefined}
                                  data-testid="personal-province"
                                />
                              </>
                            );
                          })()}
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs">{tForm.state}*</label>
                          {/* Residence country: searchable typeahead with all
                              193 ISO countries. Fabrizio 24/07/2026 — un
                              cliente cinese residente in Italia (o viceversa)
                              deve poter selezionare il suo paese di residenza
                              senza limitazioni. Include IT + tutti i paesi
                              esteri (a differenza del birth_country picker
                              che esclude IT perché la checkbox "Nato all'estero"
                              gestisce quel caso separatamente). */}
                          <CountryCombobox
                            value={personalData.address_country}
                            onChange={(code) => setPersonalData({...personalData, address_country: code || 'IT'})}
                            lang={activeLang}
                            required
                            testId="personal-address-country"
                          />
                        </div>
                      </div>

                      {/* Date + place + province of Birth — ONLY for private customers
                          (companies have no DOB). Req. Zorzutti Stand-up 22/05/2026
                          punto 13: aggiunto campo "Provincia di nascita" mancante.
                          Req. Mara Capelli Stand-up 15/06/2026: rispetta la
                          policy ``required_fields`` impostata dall'admin per
                          paese — i campi NON sono più sempre obbligatori.
                          Req. Zorzutti feedback 27/07/2026: gestione "Nato
                          all'estero" — checkbox + dropdown paese ISO 3166.
                          Se ≠ IT nasconde la provincia (non applicabile). */}
                      {!isBusiness && (() => {
                        // Fabrizio bug 04/08/2026 (cliente polacco): il flag
                        // "Nato all'estero" era IT-centric — usava `birth_country
                        // !== 'IT'` e la dropdown escludeva IT, così un cliente
                        // residente in PL nato in Italia non poteva selezionarla.
                        // Ora "estero" = birth_country diverso dal paese di
                        // residenza dell'utente (address_country).
                        const residence = personalData.address_country || 'IT';
                        const isForeignBirth = (personalData.birth_country || residence) !== residence;
                        return (
                          <>
                            {/* Checkbox "Nato all'estero" */}
                            <div className="flex items-center gap-3 pt-1">
                              <input
                                type="checkbox"
                                id="born-abroad-checkbox"
                                checked={isForeignBirth}
                                onChange={e => {
                                  const checked = e.target.checked;
                                  setPersonalData(prev => ({
                                    ...prev,
                                    // On toggle ON: seed with a country that is
                                    // GUARANTEED to be foreign (≠ residence).
                                    // For Italian residents → default US (very
                                    // common expat-of-italian birthplace); for
                                    // any other residence → default IT (very
                                    // common: Italian-born customer living
                                    // abroad). The user then refines via the
                                    // searchable dropdown.
                                    // On toggle OFF: reset to the residence
                                    // country (was hardcoded 'IT' — wrong for
                                    // foreign customers).
                                    birth_country: checked
                                      ? ((prev.address_country || 'IT') === 'IT' ? 'US' : 'IT')
                                      : (prev.address_country || 'IT'),
                                    // Clear provincia when going abroad (not applicable),
                                    // do NOT touch it when going back to IT (let the user
                                    // re-fill if needed).
                                    birth_province: checked ? '' : prev.birth_province,
                                  }));
                                }}
                                className="w-5 h-5 rounded border-gray-700 bg-[#111] text-lojack-red focus:ring-lojack-red focus:ring-offset-0 cursor-pointer"
                                data-testid="personal-born-abroad-checkbox"
                              />
                              <label htmlFor="born-abroad-checkbox" className="text-gray-300 text-sm cursor-pointer select-none">
                                {tForm.born_abroad || 'Nato all\'estero'}
                              </label>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <label className="text-gray-400 text-xs">{tForm.birth_date}{countryPolicy.birth_date ? '*' : ''}</label>
                                <DatePickerField
                                  value={personalData.birth_date}
                                  onChange={(v) => setPersonalData({...personalData, birth_date: v})}
                                  locale={activeLang}
                                  maxDate={new Date()}
                                  minDate={new Date(1900, 0, 1)}
                                  testId="personal-birth-date"
                                  required={countryPolicy.birth_date}
                                />
                              </div>
                              <div>
                                <label className="text-gray-400 text-xs">
                                  {isForeignBirth ? (tForm.birth_city_abroad || 'Città di nascita') : tForm.birth_place}
                                  {countryPolicy.birth_place ? '*' : ''}
                                </label>
                                <input
                                  value={personalData.birth_place}
                                  onChange={e => setPersonalData({...personalData, birth_place: e.target.value})}
                                  className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none"
                                  required={countryPolicy.birth_place}
                                  data-testid="personal-birth-place"
                                  placeholder={isForeignBirth ? 'es. Buenos Aires' : tForm.birth_place_example}
                                />
                              </div>
                              <div>
                                {isForeignBirth ? (
                                  <>
                                    <label className="text-gray-400 text-xs">
                                      {tForm.birth_country || 'Paese di nascita'}
                                      {countryPolicy.birth_place ? '*' : ''}
                                    </label>
                                    {/* Searchable typeahead among all 193 ISO
                                        countries. Fabrizio bug 24/07/2026
                                        (Bohdan Slutinsky): the previous
                                        <select> exposed only 35 paesi da
                                        COUNTRY_PREFIXES (mancavano BY, UA,
                                        MD, KZ, ecc.).
                                        Fabrizio bug 04/08/2026 (cliente
                                        polacco): excludeCode era "IT"
                                        hardcoded → un residente polacco
                                        nato in Italia non trovava IT nella
                                        lista. Ora escludiamo il paese di
                                        residenza dell'utente (l'unico che
                                        NON ha senso vedere quando si dichiara
                                        "nato all'estero"). */}
                                    <CountryCombobox
                                      value={personalData.birth_country}
                                      onChange={(code) => setPersonalData({
                                        ...personalData,
                                        birth_country: code || (personalData.address_country || 'IT'),
                                      })}
                                      lang={activeLang}
                                      excludeCode={personalData.address_country || 'IT'}
                                      required={countryPolicy.birth_place}
                                      testId="personal-birth-country"
                                    />
                                  </>
                                ) : (() => {
                                  // Birth-province field uses the SAME country-aware
                                  // strategy. We use the customer's residence country
                                  // as proxy (95% of customers born in the same
                                  // country where they currently live).
                                  const cfg = getProvinceConfig(personalData.address_country, activeLang);
                                  return (
                                    <>
                                      <label className="text-gray-400 text-xs">{tForm.birth_province}{countryPolicy.birth_place ? '*' : ''}</label>
                                      <input
                                        value={personalData.birth_province}
                                        onChange={e => setPersonalData({
                                          ...personalData,
                                          birth_province: cfg.autoUppercase ? e.target.value.toUpperCase() : e.target.value,
                                        })}
                                        className={`w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none ${cfg.autoUppercase ? 'uppercase' : ''}`}
                                        required={countryPolicy.birth_place}
                                        maxLength={cfg.maxLength}
                                        placeholder={cfg.placeholder}
                                        pattern={cfg.pattern || undefined}
                                        title={cfg.helperText || undefined}
                                        data-testid="personal-birth-province"
                                      />
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </>
                        );
                      })()}

                      {/* DATI INSERITI DAL DEALER block has been moved to TOP
                          of the form as a dedicated widget (req. Zorzutti
                          Stand-up 22/05/2026). */}

                      <div>
                        <label className="text-gray-400 text-xs">{tForm.phone2}</label>
                        <div className="mt-1">
                          <PhoneInput
                            prefix={personalData.phone2_prefix}
                            number={personalData.phone2}
                            onChange={({ prefix, number }) => setPersonalData({ ...personalData, phone2_prefix: prefix, phone2: number })}
                            testId="personal-phone2"
                            placeholder={tForm.placeholders?.phone || "es. 3401234567"}
                            customerCountry={personalData.address_country || customer?.country}
                            uiLang={activeLang}
                          />
                        </div>
                      </div>

                      <div className="flex gap-3 pt-2">
                        <button type="submit" disabled={savingData} className="w-full btn-lojack flex items-center justify-center gap-2 disabled:opacity-50" data-testid="personal-data-submit">
                          {savingData ? tForm.saving : tForm.submit}
                        </button>
                      </div>
                    </>
                  );
                })()}
              </form>
            </div>
          </div>
        )}

        {/* STEP: GDPR Preferences (between personal data and contract signature) */}
        {walletStep === 'preferences' && (
          <div className="max-w-2xl mx-auto animate-fade-in" data-testid="step-preferences">
            <SigningStepper currentStep={2} translations={tForm} />
            <div className="wallet-card">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lojack-red text-xs uppercase tracking-wider font-semibold">
                  {tForm.preferences_step_label}
                </span>
              </div>
              <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
                <Shield size={20} className="text-lojack-red" />
                {tForm.preferences_title}
              </h2>
              <p className="text-gray-400 text-sm mb-4">{tForm.preferences_subtitle}</p>
              <GDPRPreferences
                customerId={customerId}
                customerEmail={customer?.email}
                country={activeLang}
                customer={customer}
                contractPdfUrl={contractPdfUrl}
                privacyPolicyUrl={`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}/privacy-policy/preview`}
                onComplete={async () => {
                  // Req. Zorzutti Stand-up 22/05/2026 punto 10: evitare
                  // "schermata nera" tra preferenze salvate e contratto.
                  // Refresh customer PRIMA di cambiare step, così
                  // ContractModal renderizza con i dati gia aggiornati
                  // (gdpr_preferences) e niente flash buio.
                  toast.success(tForm.preferences_saved);
                  try { await fetchCustomer(); } catch (_) { /* non-blocking */ }
                  setWalletStep('contract');
                }}
              />
              <div className="flex gap-3 pt-4 mt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => setWalletStep('form')}
                  className="flex-1 py-3 border border-gray-600 rounded-xl text-gray-400 hover:bg-gray-800 transition-colors"
                  data-testid="preferences-back-btn"
                >
                  {tForm.back}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Personal Data Confirmation Popup */}
        {showDataConfirm && (
          // Req. A. Zorzutti Stand-up 04/06/2026 pt. 10 — il click sul
          // backdrop NON deve chiudere la pop-up di riepilogo dati. La
          // chiusura ora avviene SOLO via il pulsante X o "Modifica".
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
            style={{width:'100vw',height:'100vh',top:0,left:0}}
            onClick={(e) => e.stopPropagation()}
            data-testid="data-confirm-overlay"
          >
            <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-lg border border-gray-700 overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-gray-700">
                <h2 className="text-lg font-bold text-white" data-testid="data-confirm-title">{tForm.confirm_data_title}</h2>
                <button onClick={() => setShowDataConfirm(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button>
              </div>
              <div className="p-5 space-y-2 max-h-[60vh] overflow-y-auto">
                <p className="text-gray-400 text-sm mb-3">{tForm.confirm_data_subtitle}</p>
                {/* Fabrizio CR 06/08/2026 — pre-signature freeze warning:
                    let the customer know that some fields will become
                    non-editable after signing. Shows before the readonly
                    data recap so it's the first thing they see. */}
                {tForm.data_confirm_warning && (
                  <div
                    className="bg-amber-950/30 border border-amber-800/50 rounded-lg p-3 mb-3 text-xs text-amber-100/90 leading-relaxed"
                    data-testid="data-confirm-warning"
                  >
                    ⚠️ {tForm.data_confirm_warning}
                  </div>
                )}
                {(() => {
                  // Conditional confirmation rows: business customers see
                  // company name + VAT only (no first_name/last_name, no
                  // birth fields, no fiscal code); private customers see
                  // first_name/last_name + birth fields + fiscal code only.
                  // Req. A. Zorzutti Stand-up 04/06/2026 — Feedback Fase 3
                  // Pag 7 (hide campi Business vs Privato condizionali).
                  const isBusiness = (customer?.customer_type || 'private') === 'business';
                  const rows = [];
                  if (isBusiness) {
                    rows.push([tForm.confirm_company, personalData.company_name || '-']);
                  } else {
                    rows.push([tForm.first_name, personalData.first_name]);
                    rows.push([tForm.last_name, personalData.last_name]);
                  }
                  rows.push([tForm.confirm_address, `${personalData.address} ${personalData.civic_number || ''}`.trim()]);
                  rows.push(['CAP', personalData.zip_code]);
                  rows.push([tForm.city, personalData.city]);
                  rows.push([tForm.province, personalData.province]);
                  rows.push([tForm.state, getLocalizedCountryName(personalData.address_country, activeLang) || personalData.address_country]);
                  if (!isBusiness) {
                    rows.push([tForm.birth_date, personalData.birth_date ? formatLocalizedDate(personalData.birth_date, activeLang) : '-']);
                    const isForeignBirth = (personalData.birth_country || 'IT') !== 'IT';
                    if (isForeignBirth) {
                      rows.push([tForm.birth_city_abroad || tForm.birth_place, personalData.birth_place]);
                      rows.push([tForm.birth_country || 'Paese di nascita', getLocalizedCountryName(personalData.birth_country, activeLang) || personalData.birth_country]);
                    } else {
                      rows.push([tForm.birth_place, personalData.birth_place]);
                      rows.push([tForm.birth_province, personalData.birth_province || '-']);
                    }
                    rows.push([tForm.confirm_tax_label, personalData.tax_code]);
                  } else {
                    rows.push([tForm.confirm_vat_label, personalData.vat_number || '-']);
                  }
                  rows.push(['Email', customer?.email]);
                  rows.push([tForm.phone1, `${customer?.phone_prefix || ''} ${customer?.phone || ''}`.trim()]);
                  rows.push([tForm.phone2_short, personalData.phone2 ? `${personalData.phone2_prefix} ${personalData.phone2}` : '-']);
                  return rows.map(([label, value]) => (
                    <div key={label} className="flex justify-between items-center py-2 border-b border-gray-800" data-testid={`confirm-row-${label}`}>
                      <span className="text-gray-500 text-sm">{label}</span>
                      <span className="text-white text-sm font-medium text-right max-w-[60%]">{value}</span>
                    </div>
                  ));
                })()}
              </div>
              <div className="p-5 flex flex-col gap-3 border-t border-gray-700">
                {/* Fabrizio CR 06/08/2026 — mandatory explicit-consent
                    checkbox before proceeding. The submit button is
                    disabled until the checkbox is ticked. */}
                <label
                  className="flex items-start gap-3 cursor-pointer select-none"
                  data-testid="data-confirm-checkbox-label"
                >
                  <input
                    type="checkbox"
                    checked={dataConfirmChecked}
                    onChange={e => setDataConfirmChecked(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-700 bg-[#111] text-lojack-red focus:ring-lojack-red focus:ring-offset-0 cursor-pointer mt-0.5 flex-shrink-0"
                    data-testid="data-confirm-checkbox"
                  />
                  <span className="text-gray-300 text-sm leading-relaxed">
                    {tForm.data_confirm_checkbox || 'I confirm my data is correct.'}
                  </span>
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      // Fabrizio CR 07/02/2026 — "Modifica" ora apre
                      // direttamente il correction modal ("Segnala errore
                      // al concessionario") invece di riportare al form.
                      // Se il cliente clicca Modifica è perché ha visto
                      // un dato sbagliato tra quelli precompilati dal
                      // dealer — l'unica strada per correggerli è la
                      // richiesta di correzione, che blocca la procedura
                      // e notifica il dealer.
                      setShowDataConfirm(false);
                      setDataConfirmChecked(false);
                      setShowCorrectionModal(true);
                    }}
                    className="flex-1 py-2.5 border border-gray-600 rounded-xl text-gray-400 hover:bg-gray-800 transition-colors"
                    data-testid="data-confirm-cancel"
                  >
                    {tForm.edit_btn}
                  </button>
                  <button
                    onClick={handlePersonalDataSubmit}
                    disabled={savingData || !dataConfirmChecked}
                    className="flex-1 btn-lojack flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="data-confirm-submit"
                  >
                    {savingData ? '...' : tForm.confirm_and_continue}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP: CONTRACT / GDPR / OTP SIGNING */}
        {walletStep === 'contract' && (
          <div className="max-w-2xl mx-auto animate-fade-in" data-testid="contract-step">
            <SigningStepper
              currentStep={3}
              readingProgressPct={contractReadPct}
              translations={tForm}
            />

            <ContractModal
              showTerms={true}
              onReadProgress={setContractReadPct}
              // Req. Zorzutti mockup 08/07/2026 punto 4: il bottone in fondo
              // allo step Contratto è "Indietro" e torna allo step precedente
              // (informativa privacy), non più all'anagrafica.
              setShowTerms={(val) => { if (!val) setWalletStep('preferences'); }}
              customer={customer}
              customerId={customerId}
              uiLanguage={activeLang}
              termsAccepted={termsAccepted}
              setTermsAccepted={setTermsAccepted}
              contractPdfUrl={contractPdfUrl}
              signingContract={signingContract}
              setSigningContract={setSigningContract}
              otpDeliveryChannel={countryPolicy.otp_delivery_channel}
              setContractSigned={(val) => {
                setContractSigned(val);
                if (val) setWalletStep('complete');
              }}
              fetchCustomer={fetchCustomer}
              inline={true}
            />
          </div>
        )}

        {/* STEP: COMPLETE */}
        {walletStep === 'complete' && (
          <div className="animate-fade-in">
            <div className="max-w-2xl mx-auto mb-2">
              <SigningStepper currentStep={4} translations={tForm} />
            </div>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-lojack-blue-600/20 border-2 border-lojack-blue-500 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-lojack-blue-500" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" data-testid="complete-greeting">
                {translations.congratulations || translations.welcome}, {getCustomerGreetingName(customer) || translations.welcome}!
              </h1>
              <p className="text-gray-400 text-lg">
                {translations.card_below || `${translations.your_card} ${translations.vehicle_protection}`}
              </p>
              {customer?.contract_signed && (
                <p className="text-lojack-blue-400 text-sm mt-2">
                  {tForm.contract_signed_msg}
                </p>
              )}
            </div>

            {/* P12.2 (Mara stand-up 25/05/2026): prominent
                operating-centre picker positioned BETWEEN the
                "Complimenti / La tua card" header and the digital
                card visual. Lets the customer change which LoJack
                Operating Centre serves them (phone number on the
                card + vCard contact). Does NOT change the page
                language — that one is locked at signature time. */}
            <div className="max-w-md mx-auto mb-6" data-testid="soc-picker-wrapper">
              <div className="relative">
                <div className="absolute -inset-[1px] bg-gradient-to-r from-lojack-red/30 via-lojack-red/50 to-lojack-red/30 rounded-2xl blur-[2px] animate-pulse" style={{ animationDuration: '3s' }} />
                <div className="relative bg-[#141414] border border-gray-700/60 rounded-2xl px-5 py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-lojack-red/15 border border-lojack-red/30 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-lojack-red" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-[0.15em] text-white font-semibold">
                          {tForm.operation_center_label || 'Operating centre'}
                        </p>
                        <p className="text-[10px] text-gray-100 mt-0.5">
                          {tForm.operation_center_help || 'Select your reference operating centre'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-2xl">{COUNTRY_OPTIONS.find(c => c.code === (customer?.country || activeLang))?.flag}</span>
                      <select
                        value={customer?.country || activeLang}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        disabled={changingCountry}
                        className="bg-[#1E1E1E] text-white text-sm border border-gray-600 rounded-lg px-3 py-2 pr-8 cursor-pointer outline-none focus:border-lojack-red transition-colors appearance-none"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23E31837' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                        data-testid="soc-selector-complete"
                      >
                        {[...COUNTRY_OPTIONS]
                          .sort((a, b) => {
                            if (a.code === 'IT') return -1;
                            if (b.code === 'IT') return 1;
                            return getLocalizedCountryName(a.code, activeLang)
                              .localeCompare(getLocalizedCountryName(b.code, activeLang));
                          })
                          .map(c => (
                            <option key={c.code} value={c.code} style={{ backgroundColor: '#1A1A1A', color: '#fff' }}>
                              {c.flag} {getLocalizedCountryName(c.code, activeLang)}
                            </option>
                          ))}
                      </select>
                      {changingCountry && <div className="spinner-small" />}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DigitalCard operationCenter={operationCenter} walletUrls={walletUrls} customerId={customerId} translations={translations} uiSocLabel={tForm.soc_label_24_7} />

            <WalletButtons
              translations={translations}
              addToWallet={addToWallet}
              googleWalletLoading={googleWalletLoading}
              googleWalletUrl={googleWalletUrl}
              appleWalletLoading={appleWalletLoading}
              applePassReady={applePassReady}
              customerId={customerId}
              customer={customer}
              operationCenter={operationCenter}
              uiLanguage={activeLang}
            />

            <div className="wallet-card">
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Phone className="text-lojack-red" size={20} />
                {translations.save_number}
              </h4>
              <p className="text-gray-400 mb-4">
                {translations.save_number_desc}
              </p>
              {/* On desktop: 2-column layout with action buttons on the
                  left and a scannable vCard QR on the right. On mobile:
                  stacked, QR hidden (user cannot scan their own screen).
                  Mirror del pattern Apple/Google Wallet QR.
                  Fabrizio req. 23/02/2026. */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex flex-wrap gap-3">
                  <a
                    href={`tel:${operationCenter?.phone || walletUrls?.operation_center?.phone || '+39 02 36 58 9333'}`}
                    className="btn-lojack-outline inline-flex items-center gap-2"
                    data-testid="call-center-btn"
                  >
                    <Phone size={18} />
                    {translations.call}: {operationCenter?.phone || walletUrls?.operation_center?.phone || '+39 02 36 58 9333'}
                  </a>
                  <a
                    href={`${API}/customers/${customerId}/vcard`}
                    download
                    className="btn-lojack inline-flex items-center gap-2"
                    data-testid="save-contact-btn"
                  >
                    <Download size={18} />
                    {tForm.save_to_contacts}
                  </a>
                </div>
                {(() => {
                  const vcard = _buildOperationCenterVcard(operationCenter);
                  if (!vcard || !_isDesktopForQr()) return null;
                  return (
                    <div
                      className="flex flex-col items-center gap-2 md:pl-4 md:border-l md:border-gray-700"
                      data-testid="oc-vcard-qr-container"
                    >
                      <span className="text-xs uppercase tracking-wider text-gray-400">
                        {tForm.scan_to_save_contact || translations.scan_to_save_contact || "Scansiona per salvare"}
                      </span>
                      <div className="bg-white p-2 rounded-md">
                        <QRCodeSVG
                          value={vcard}
                          size={120}
                          level="M"
                          includeMargin={false}
                          data-testid="oc-vcard-qr"
                        />
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-lojack-dark border-t border-gray-800 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-12" style={{filter:'drop-shadow(0 0 8px rgba(255,255,255,0.3))'}} />
          </div>
          <p className="text-gray-500 text-sm mb-4">
            &copy; {new Date().getFullYear()} LoJack. {translations.all_rights}.
          </p>
          <TelcaVoIPBadge />
        </div>
      </footer>

      {/* Correction request modal — rendered outside the main flow
          so it can overlay any step. Only mounted while open to
          avoid needless React tree churn. */}
      {showCorrectionModal && customer && (
        <CorrectionRequestModal
          customer={customer}
          onClose={() => setShowCorrectionModal(false)}
          onSubmitted={() => {
            // Force a fresh fetch so the pending state kicks in.
            fetchCustomer();
          }}
          translations={{
            title: tForm.correction_title,
            subtitle: tForm.correction_subtitle,
            field_label: tForm.correction_field_label,
            current_label: tForm.correction_current_label,
            new_label: tForm.correction_new_label,
            note_label: tForm.correction_note_label,
            note_placeholder: tForm.correction_note_placeholder,
            submit: tForm.correction_submit,
            cancel: tForm.correction_cancel,
            warning_title: tForm.correction_warning_title,
            warning_body: tForm.correction_warning_body,
            success_toast: tForm.correction_success_toast,
            validation_note: tForm.correction_validation_note,
            // Double-entry confirmation (Zorzutti CR 01/08/2026)
            confirm_label: tForm.correction_confirm_label,
            confirm_placeholder: tForm.correction_confirm_placeholder,
            validation_mismatch: tForm.correction_validation_mismatch,
            match_hint_ok: tForm.correction_match_hint_ok,
            // Field-picker labels (Zorzutti CR 03/08/2026 — no more IT leak)
            field_email: tForm.correction_field_email,
            field_phone: tForm.correction_field_phone,
            field_tax_code: tForm.correction_field_tax_code,
          }}
        />
      )}
    </div>
  );
};

export default CustomerWalletPage;
